import React from 'react';
import RingilDialog from '@components/framework/dialog/RingilDialog.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { dayjsFromLocalDateString, formatDateFromDayjsWithLocale } from '@utils/dateWithTz.tsx';
import { StopMergeCandidate } from '@app/pages/expedition/create/stopMerge.ts';

interface ComponentProps {
  visible: boolean;
  /** Position of the stop being folded away, for naming it the way the route does. */
  locIndex: number;
  candidates: StopMergeCandidate[];
  onHide: () => void;
  onMerge: (targetIndex: number) => void;
}

/** The days a stop is wanted on, shortened to the first and last when there are several. */
const formatDays = (days: string[]): string => {
  const formatted = days.map(day => formatDateFromDayjsWithLocale(dayjsFromLocalDateString(day))).filter(Boolean);
  if (formatted.length <= 1) return formatted[0] ?? '—';
  return `${formatted[0]} – ${formatted[formatted.length - 1]}`;
};

/**
 * Picks the stop at the same place that this one is folded into.
 *
 * Each candidate carries its own button, so choosing the stop is also the confirmation: one stop or
 * several, the user sees the date that will stay before anything is removed.
 */
const MergeStopDialog: React.FC<ComponentProps> = ({ visible, locIndex, candidates, onHide, onMerge }) => {
  const { tr } = useTranslationLgs();

  return (
    <RingilDialog
      visible={visible}
      onHide={onHide}
      heading={tr('CreateExpedition.mergeStopHeading', 'Merge with the same location')}
      buttons={<RingilButton mode={'outlined'} label={tr('generic.cancel', 'Cancel')} onClick={onHide} dataTestId={'mergeStopCancel'} />}
    >
      <div className={'flex flex-col gap-4'}>
        <span className={'text-content-secondary'}>
          {tr(
            'CreateExpedition.mergeStopExplanation',
            'The orders of stop {{stop}} move to the stop you choose, and stop {{stop}} is removed. The chosen stop keeps its date and contacts.',
            { stop: locIndex + 1 },
          )}
        </span>

        <div className={'flex flex-col gap-2'} data-testid={'mergeStopCandidates'}>
          {candidates.map(candidate => (
            <div
              key={candidate.index}
              className={'flex flex-row items-center justify-between gap-4 rounded border border-solid border-content-quaternary bg-base-tertiary px-3 py-2'}
              data-testid={`mergeStopCandidate-${candidate.index}`}
            >
              <div className={'flex flex-col'}>
                <strong>{tr('CreateExpedition.mergeStopCandidateName', 'Stop {{stop}} · {{name}}', { stop: candidate.index + 1, name: candidate.name })}</strong>
                <span className={'text-content-secondary'}>
                  {formatDays(candidate.days)} · {tr('CreateExpedition.mergeStopCandidateOrders', '{{count}} orders', { count: candidate.orderCount })}
                </span>
              </div>
              <RingilButton
                mode={'filled-rectangle'}
                size={'small'}
                label={tr('CreateExpedition.mergeStopConfirm', 'Merge here')}
                onClick={() => onMerge(candidate.index)}
                dataTestId={`mergeStopCandidate-${candidate.index}-merge`}
              />
            </div>
          ))}
        </div>
      </div>
    </RingilDialog>
  );
};

export default MergeStopDialog;
